import DOMPurify from 'dompurify'
import type { Config, UponSanitizeAttributeHookEvent } from 'dompurify'

// Only wrapRaw() may write these: coming from stored HTML they would smuggle markup past the sanitizer.
const EDITOR_ATTRIBUTE_PREFIX = 'data-hop-'
// ProseMirror marks the HTML it copies with it; read on paste, it is never part of the content.
export const CLIPBOARD_SLICE_ATTRIBUTE = 'data-pm-slice'
const URL_ATTRIBUTES = new Set(['src', 'href', 'xlink:href'])
const DATA_URL = /^data:/i
const DATA_IMAGE_URL = /^data:image\//i

const CONFIG: Config & { RETURN_DOM: true } = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder'],
  FORBID_TAGS: ['style', 'noscript'],
  RETURN_DOM: true,
}

const purifier = DOMPurify()
purifier.setConfig(CONFIG)

const isEditorAttribute = (name: string) => name.toLowerCase().startsWith(EDITOR_ATTRIBUTE_PREFIX)

// Browsers skip control characters and spaces when they read a URL scheme.
const withoutSchemeNoise = (value: string) => [...value].filter(character => character.charCodeAt(0) > 0x20).join('')

const isNonImageDataUrl = (name: string, value: string) => {
  const url = withoutSchemeNoise(value)
  return URL_ATTRIBUTES.has(name.toLowerCase()) && DATA_URL.test(url) && !DATA_IMAGE_URL.test(url)
}

const rejectAttribute = (_node: Element, data: UponSanitizeAttributeHookEvent) => {
  if (isEditorAttribute(data.attrName) || isNonImageDataUrl(data.attrName, data.attrValue)) {
    data.keepAttr = false
  }
}

const linksWithHref = new Set<Element>()

const rememberLinkWithHref = (node: Node) => {
  if (node instanceof Element && node.tagName === 'A' && node.hasAttribute('href')) {
    linksWithHref.add(node)
  }
}

purifier.addHook('uponSanitizeAttribute', rejectAttribute)
purifier.addHook('beforeSanitizeAttributes', rememberLinkWithHref)

// A link that loses an unsafe href keeps its text, as TinyMCE did.
const unwrapLinksThatLostTheirHref = () =>
  linksWithHref.forEach(link => {
    if (!link.hasAttribute('href')) {
      link.replaceWith(...link.childNodes)
    }
  })

export const sanitizeHtml = (html: string): HTMLElement => {
  try {
    // RETURN_DOM hands back the <body> of the parsed document.
    const body = purifier.sanitize(html, CONFIG) as HTMLElement
    unwrapLinksThatLostTheirHref()
    return body
  } finally {
    linksWithHref.clear()
  }
}

export const isSafeAttribute = (name: string, value: string, tagName: string) =>
  !isEditorAttribute(name) &&
  name.toLowerCase() !== CLIPBOARD_SLICE_ATTRIBUTE &&
  !isNonImageDataUrl(name, value) &&
  purifier.isValidAttribute(tagName.toLowerCase(), name.toLowerCase(), value)
